import { useCallback, useMemo, useEffect } from 'react';
import { useUpsertClassUserMutation } from '@/apollo/gql/generated/graphql';
import { useRouter } from 'next/router';
import { useViewerContext } from '@/contexts/useViewer';
import { useLoggingContext } from '@/contexts/useLogging';
import { customBugsnagLogger } from '@/utils/bugsnag';
import { useRecoilValue } from 'recoil';
import { isUserLoggingOutAtom } from '@/recoil/atom/globalState';
import { events } from '@/config/log_events';
import CookieService from '@/services/cookies';

const useUserStatusManager = () => {
	const [upsertClassUser] = useUpsertClassUserMutation();
	const router = useRouter();
	const slug = useMemo(() => router?.query?.id, [router]);
	const isUserLoggingOut = useRecoilValue(isUserLoggingOutAtom);
	const { viewer } = useViewerContext();
	const { logToLambda } = useLoggingContext();

	const cleanUpCookies = async () => {
		await CookieService.remove('access_token', { path: '/' });
		await localStorage.removeItem('token');
		await localStorage.removeItem('user');
		await localStorage.removeItem('tokenExp');
		await localStorage.removeItem('tokenExpirationTime');
		await localStorage.removeItem('opentok_client_id');
		CookieService.set('granted_access', '', {
			path: '/',
		});
		CookieService.set('access_token', '', {
			path: '/',
		});
		localStorage.removeItem('boardPinnedUsers');
		localStorage.removeItem('videoPinnedFeeds');
	};

	const setStatus = useCallback(
		async (userId: null, newStatus) => {
			try {
				customBugsnagLogger.debug(
					`updating user ${userId || viewer?.id} status to ${newStatus}`
				);
				await upsertClassUser({
					variables: {
						input: {
							userId: userId || viewer?.id,
							status: newStatus,
							classSlug: slug as string,
						},
					},
				});
				if (newStatus === 'logout'){
					await cleanUpCookies();
				}
			} catch (error) {
				console.error('Error upserting class user:', error);
				customBugsnagLogger.error(error);
			}
		},
		[upsertClassUser, slug, viewer]
	);

	useEffect(() => {
		const setUserOffline = () => {
			customBugsnagLogger.debug(`setting user ${viewer?.id} offline`);
			console.log('setting user offline');
			// Where this page thinks it is. lobby→class is a full page load, so
			// this write races the new page's 'class' write; if it lands last it
			// would pin the row to 'offline' while the user is in the room (and
			// they'd vanish from the sidebar). expectedLocation lets the server
			// drop the write once the row has moved past this page.
			const leavingFrom = router.asPath.includes('lobby') ? 'lobby' : 'class';
			upsertClassUser({
				variables: {
					input: {
						userId: viewer?.id,
						role: viewer?.role,
						status: 'offline',
						classSlug: slug as string,
						handRaised: false,
						connectionStatus: 3,
						inactive: false,
						location: 'offline',
						expectedLocation: leavingFrom,
					},
				},
			});
		};

		// Only 'beforeunload'. There was also an 'onunload' registration, but
		// 'onunload' is not an event type — the event is 'unload', and onunload is
		// the property form — so that listener never fired. Note it therefore
		// cannot have been the source of the paired teardown writes seen in
		// production (identical offline writes 78-411ms apart); that is still open.
		window.addEventListener('beforeunload', setUserOffline);

		return () => {
			window.removeEventListener('beforeunload', setUserOffline);
			// setUserOffline();
		};
	}, [upsertClassUser, slug, viewer?.id, viewer?.role, router.asPath]);

	useEffect(() => {
		if (isUserLoggingOut) {
			logToLambda(events.LOGOUT, { user: viewer });
			setStatus(null, 'logout');
		}
	}, [setStatus, isUserLoggingOut, logToLambda, viewer]);

	return { setStatus };
};

export default useUserStatusManager;
